import { useCallback } from "react"
import { useMutation } from "urql"

import type {
  DeleteAssociationLocationMutation,
  DeleteAssociationLocationMutationVariables,
} from "@/graphql/codegen/graphql"
import { DeleteAssociationLocationDocument } from "@/graphql/codegen/graphql"
import { NotificationType } from "@/schemas/notification"
import { useNotificationsStore } from "@/stores/useNotificationsStore"

interface UseDeleteAssociationLocationReturn {
  deleteLocation: (id: string) => Promise<boolean>
  isDeleting: boolean
}

export const useDeleteAssociationLocation = (): UseDeleteAssociationLocationReturn => {
  const notify = useNotificationsStore((state) => state.enqueueNotification)

  const [{ fetching }, executeMutation] = useMutation<
    DeleteAssociationLocationMutation,
    DeleteAssociationLocationMutationVariables
  >(DeleteAssociationLocationDocument)

  const deleteLocation = useCallback(
    async (id: string): Promise<boolean> => {
      const { data, error } = await executeMutation({ id })
      const output = data?.companyProductAssociation.deleteLocation

      const hasError = error || output?.__typename === "DeleteAssociationLocationFailure"

      if (hasError) {
        const message =
          output?.__typename === "DeleteAssociationLocationFailure" ? output.error.message : error?.message
        notify({
          type: NotificationType.Error,
          message: message ?? "Failed to remove location. Please try again.",
        })
        return false
      }

      notify({
        type: NotificationType.Success,
        message: "Location removed",
      })
      return true
    },
    [executeMutation, notify]
  )

  return { deleteLocation, isDeleting: fetching }
}
